/**
 * Frontend performance budgets + lightweight route-transition profiling.
 *
 * Two responsibilities:
 *
 *   1. Source of truth for the frontend perf budgets that
 *      ``docs/PERF_BUDGETS.md`` and the bundle-size CI check key off of.
 *      The bundle-size check (``frontend/scripts/check-bundle-size.mjs``)
 *      reads the same constants so the budget can be edited in exactly one
 *      place.
 *
 *   2. A tiny ``observeRouterPerf`` helper that hooks the TanStack Router
 *      subscribe API and logs route-transition durations using
 *      ``performance.now()``.  We deliberately don't pull in
 *      ``web-vitals`` or any other library — the budgets we care about are
 *      transition latency (router state change → render commit) and the
 *      initial JS payload, both of which we can measure with the browser's
 *      built-in Performance API.
 *
 * Privacy:
 *   - The route-transition logger emits only the route ``id`` (TanStack
 *     Router's static template — e.g. ``/_authenticated/bosun``) and
 *     the duration in ms.  No URL params, no search-string, no header
 *     values, and never any project secrets, task output, or field values.
 *   - On Sentry-enabled builds we also emit a breadcrumb with the same
 *     scrubbed fields.
 */
import * as Sentry from '@sentry/react'
import type { AnyRouter } from '@tanstack/react-router'

/** Initial JS payload (gzipped) budgets, in bytes. */
export const FRONTEND_PERF_BUDGETS = {
  /** Sum of the entry chunk + every chunk it eagerly imports. */
  initialJsGzipBytes: 600 * 1024,
  /** Single largest non-entry chunk (lazy route bundle ceiling). */
  largestLazyChunkGzipBytes: 400 * 1024,
  /** Sum of every CSS chunk emitted at build time. */
  totalCssGzipBytes: 150 * 1024,
  /**
   * Route-transition wall-clock budget, milliseconds. Measured from when
   * TanStack Router transitions to ``isLoading=true`` to when it returns
   * to ``isLoading=false`` (i.e. queries settled + new route rendered).
   * Not enforced in CI — gated manually via the docs.
   */
  routeTransitionP95Ms: 400,
  /** Time-to-first-byte budget for the app's initial HTML load. Manual. */
  appLoadTtfbP95Ms: 400,
  /**
   * Largest contentful paint p75 budget. Web-Vitals "good" threshold is
   * 2500 ms — we aim for 2 s so we have head-room before users notice.
   * Manual / Lighthouse.
   */
  appLoadLcpP75Ms: 2_000,
} as const

export type FrontendPerfBudgets = typeof FRONTEND_PERF_BUDGETS

/**
 * The narrow subset of TanStack Router we need: a typed ``subscribe`` plus
 * read-only access to ``state.matches``. We declare this as
 * ``Pick<AnyRouter, ...>`` so the real router instance from
 * ``createRouter`` is directly assignable — no cast, no structural-mirror
 * interface to drift away from the upstream types. The matched-route
 * descriptors live on ``router.state.matches`` (not on the navigation
 * event payload, which only carries ``fromLocation`` / ``toLocation``), so
 * the observer reads them from state on each tick.
 */
export type RouterPerfTarget = Pick<AnyRouter, 'subscribe' | 'state'>

type TransitionLogger = (entry: {
  event: 'route_transition'
  route: string
  durationMs: number
  overBudget: boolean
}) => void

/**
 * Subscribe to a TanStack Router and emit a structured perf log entry
 * each time a route transition resolves. Returns the unsubscribe fn.
 *
 * The default logger writes to ``console.debug`` (so it stays out of
 * production user-visible output) and to a Sentry breadcrumb if Sentry
 * is initialized.  Pass a custom logger for tests.
 *
 * No PII is logged — only the matched route template (or "unknown") and
 * the duration. Pathname is intentionally NOT recorded because route
 * params like ``/dashboard/bosun/<uuid>`` would leak project ids.
 */
export function observeRouterPerf(
  router: RouterPerfTarget | null | undefined,
  logger: TransitionLogger = defaultTransitionLogger,
): () => void {
  if (!router) return () => {}
  let startedAt: number | null = null
  let pendingRouteId: string | null = null

  const unsubBefore = router.subscribe('onBeforeLoad', () => {
    startedAt = performanceNow()
    pendingRouteId = leafRouteId(router.state.matches)
  })

  const unsubResolved = router.subscribe('onResolved', () => {
    if (startedAt == null) return
    const durationMs = performanceNow() - startedAt
    // Prefer the post-resolve matches (more accurate for transitions that
    // changed the leaf route mid-flight); fall back to the pre-load
    // snapshot, then to "unknown" if neither side has matched anything.
    const route = leafRouteId(router.state.matches) ?? pendingRouteId ?? 'unknown'
    startedAt = null
    pendingRouteId = null
    logger({
      event: 'route_transition',
      route,
      durationMs: Math.round(durationMs * 10) / 10,
      overBudget: durationMs > FRONTEND_PERF_BUDGETS.routeTransitionP95Ms,
    })
  })

  return () => {
    unsubBefore()
    unsubResolved()
  }
}

function leafRouteId(
  matches: ReadonlyArray<{ readonly routeId?: string | undefined }>,
): string | null {
  if (matches.length === 0) return null
  const leaf = matches[matches.length - 1]
  if (leaf && typeof leaf.routeId === 'string') return leaf.routeId
  return null
}

function defaultTransitionLogger(entry: {
  event: 'route_transition'
  route: string
  durationMs: number
  overBudget: boolean
}): void {
  if (typeof console !== 'undefined') {
    if (entry.overBudget) {
      console.warn('[perf] route transition over budget', entry)
    } else if (import.meta.env?.DEV) {
      console.debug('[perf] route transition', entry)
    }
  }
  // Sentry breadcrumb (best-effort) for prod analysis. ``addBreadcrumb`` is
  // a no-op when the SDK was never ``init``-ed (e.g. dev / test builds), so
  // we don't need to gate this on the env.
  Sentry.addBreadcrumb({
    category: 'perf',
    level: entry.overBudget ? 'warning' : 'info',
    message: 'route_transition',
    data: {
      route: entry.route,
      durationMs: entry.durationMs,
      overBudget: entry.overBudget,
    },
  })
}

function performanceNow(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}
